import { safeHref } from '@/lib/utils/url';

/**
 * 공지 글의 사진 앨범 — **글에서도 미리보기에서도 이것 하나를 쓴다**.
 *
 * 미리보기가 실제와 다르면 미리보기가 아니다. 그래서 표시 규칙을 여기 한 곳에만 두고, 공지사항
 * 화면(`NoticeBoardPage`)과 글쓰기 모달의 미리보기가 같은 컴포넌트를 부른다.
 *
 * ## 자르지 않는다
 * 정사각 칸에 `cover` 로 채우면 세로 사진의 얼굴이 잘리고 단체 사진은 가운데만 남는다 —
 * 축하 사진에서 잘려 나가는 것이 하필 사람이다(사용자 지적 2026-08-28).
 *
 * ## 크기는 상한이지 폭이 아니다
 * 글쓴이가 고른 값(`maxWidthPx`)은 **넘지 않을 폭**이다. 작은 사진을 그 폭까지 늘리지 않는다 —
 * 400px 짜리를 900px 로 키우는 것은 크기를 정한 것이 아니라 화질을 버린 것이다.
 * 고르지 않았으면 카드 폭까지 간다.
 */

export type DisplayImage = {
  id: string;
  url: string | null;
  fileName: string | null;
  maxWidthPx?: number | null;
};

/** 세로로 아주 긴 사진 하나가 화면을 통째로 먹지 않게 하는 바닥선. 폭과 달리 이건 항상 걸린다. */
const MAX_HEIGHT = 520;

export function NoticeImageList({ images, linkToOriginal = true }: { images: DisplayImage[]; linkToOriginal?: boolean }) {
  if (images.length === 0) return null;

  return (
    <div className="space-y-2">
      {images.map((image) => {
        const picture = (
          <img
            src={image.url ?? ''}
            alt={image.fileName ?? ''}
            loading="lazy"
            style={{
              // `maxWidth` 로만 막는다. `width` 를 주면 작은 사진이 늘어난다.
              maxWidth: image.maxWidthPx ? `min(${image.maxWidthPx}px, 100%)` : '100%',
              maxHeight: `${MAX_HEIGHT}px`,
              width: 'auto',
              height: 'auto',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              display: 'block',
            }}
          />
        );

        // 미리보기에서는 링크를 걸지 않는다 — 아직 저장되지 않은 사진이라 열 곳이 없다.
        return linkToOriginal ? (
          <a
            key={image.id}
            href={safeHref(image.url ?? '')}
            target="_blank"
            rel="noreferrer"
            title={image.fileName ?? undefined}
            style={{ display: 'block' }}
          >
            {picture}
          </a>
        ) : (
          <div key={image.id} title={image.fileName ?? undefined}>
            {picture}
          </div>
        );
      })}
    </div>
  );
}
